import {
  CloseCircleOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useEffect, useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {AiOutlineNodeIndex} from 'react-icons/ai';
import {FaBoxOpen, FaCheck} from 'react-icons/fa';
import {MdOutlinePendingActions} from 'react-icons/md';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../../CommonComponents/Cards';
import {TableData} from '../../../../CommonComponents/TableData';
import {
  RqCancelBatch,
  RqDeleteProduction,
  RqGetAllProductions,
  RqGetAvailableBatches,
} from '../../../../reactQuery/apis/rqProductionAPI';
import {selectAuth} from '../../../../redux/selectors';

import CreateBatchModal from './CreateBatchModal/CreateBatchModal';
import {ProductionDetails} from './ProductionDetails';
import RegisterProductionScreen from './RegisterProductionScreen/RegisterProductionScreen';

const {Text, Title} = Typography;

const ProductionScreen = () => {
  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  /* Modals state */
  const [isBatchModalVisible, setIsBatchModalVisible] = useState(false);
  const [isRegisterModalVisible, setIsRegisterModalVisible] = useState(false);

  const [batchForm] = Form.useForm();
  const [registerForm] = Form.useForm();

  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter] = useState(null);

  const [viewModal, setViewModal] = useState({visible: false, record: null});

  /* API Queries & Mutations */
  const {data: productionData, isLoading, error, refetch} = RqGetAllProductions(token);
  const {data: availableBatches = [], isLoading: batchesLoading, refetch: refetchBatches} = RqGetAvailableBatches(token);

  const deleteProductionMutation = RqDeleteProduction({
    onSuccess: () => toast.success('Production record deleted'),
    onError: err => toast.error(err?.message || 'Delete failed'),
  });

  const cancelBatchMutation = RqCancelBatch({
    onSuccess: () => toast.success('Bulk batch cancelled & raw materials restored!'),
    onError: err => toast.error(err?.message || 'Cancel failed'),
  });

  useEffect(() => {
    if (!productionData) {return;}

    const formatted = [...productionData]
      .sort((a, b) => dayjs(b.productionDate).valueOf() - dayjs(a.productionDate).valueOf())
      .map((prod, index) => ({
        key:            prod.id || prod._id,
        index:          index + 1,
        id:             prod.id || prod._id,
        lotNumber:      prod.lotNumber ?? '—',
        productionDate: prod.productionDate,
        batches:        prod.batches ?? [],
        packaging:      prod.packaging ?? [],
        status:         prod.status ?? 'ACTIVE',
      }));

    setDataSource(formatted);
  }, [productionData]);

  const filteredData = useMemo(() => {
    return dataSource.filter(row => {
      const matchSearch =
        !searchText ||
        row.lotNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
        row.packaging?.some(pkg => pkg.finalProductName?.toLowerCase().includes(searchText.toLowerCase()));

      const matchStatus = !statusFilter || row.status === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [dataSource, searchText, statusFilter]);

  const stats = useMemo(() => {
    const active    = dataSource.filter(p => p.status === 'ACTIVE').length;
    const completed = dataSource.filter(p => p.status === 'COMPLETED').length;
    const pending   = availableBatches.length;
    return {total: dataSource.length, active, completed, pending};
  }, [dataSource, availableBatches]);

  const handleDelete = async id => {
    try {
      await deleteProductionMutation.mutateAsync({token, id});
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelBatch = async id => {
    try {
      await cancelBatchMutation.mutateAsync({token, id});
    } catch (err) {
      console.error(err);
    }
  };

  const columns = [
    {
      title:     <Text className={'text-xs font-bold text-stone-50 uppercase'}>Sr. No.</Text>,
      dataIndex: 'index',
      key:       'index',
      width:     50,
      render:    v => <Text className={'text-xs text-stone-400 font-medium'}>{v}</Text>,
    },
    {
      title:     <Text className={'text-xs font-bold text-stone-50 uppercase'}>Lot Number</Text>,
      dataIndex: 'lotNumber',
      key:       'lotNumber',
      width:     140,
      render:    v => (
        <span className={'rounded-full font-mono font-semibold px-3 py-0.5 bg-gray-100 text-gray-800 text-xs'}>{v}</span>
      ),
    },
    {
      title:     <Text className={'text-xs font-bold text-stone-50 uppercase'}>Production Date</Text>,
      dataIndex: 'productionDate',
      key:       'productionDate',
      width:     160,
      defaultSortOrder: 'descend',
      sorter:    (a, b) => dayjs(a.productionDate).valueOf() - dayjs(b.productionDate).valueOf(),
      render: v =>
        v ? (
          <Space description={'vertical'} size={0}>
            <Text className={'text-sm font-semibold text-gray-700'}>{dayjs(v).format('DD MMM YYYY')}</Text>
            <Text className={'text-xs text-gray-400'}>{dayjs(v).format('HH:mm')}</Text>
          </Space>
        ) : (
          <Text type={'secondary'}>—</Text>
        ),
    },
    {
      title:  <Text className={'text-xs font-bold text-stone-50 uppercase'}>Packaged Products</Text>,
      key:    'packaging',
      width:  240,
      render: (_, record) => {
        const pkgs = record.packaging ?? [];
        if (!pkgs.length) {return <Text type={'secondary'}>—</Text>;}
        return (
          <Space description={'vertical'} size={2}>
            {pkgs.map((pkg, i) => (
              <div key={i} className={'text-left'}>
                <Text className={'font-semibold text-gray-800 text-sm block'}>{pkg.finalProductName}</Text>
                <Tag color={'blue'} className={'rounded text-xs'}>
                  {pkg.bags} bags × {pkg.bagWeight} KG ({pkg.bags * pkg.bagWeight} KG total)
                </Tag>
              </div>
            ))}
          </Space>
        );
      },
    },
    {
      title:  <Text className={'text-xs font-bold text-stone-50 uppercase'}>Batch References</Text>,
      key:    'batches',
      width:  160,
      render: (_, record) => {
        const btchs = record.batches ?? [];
        if (!btchs.length) {return <Text type={'secondary'}>—</Text>;}
        return (
          <Space wrap size={4}>
            {btchs.map((b, i) => (
              <Tag key={i} color={'purple'} className={'rounded-full text-xs font-mono font-semibold'}>
                {b.batchId}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title:  'Actions',
      key:    'action',
      width:  80,
      render: (_, record) => (
        <div className={'flex gap-2'}>
          <Tooltip title={'Delete production record'}>
            <Popconfirm
              title={'Delete this production record?'}
              description={'This action cannot be undone.'}
              onConfirm={() => handleDelete(record.id || record._id)}
              okText={'Yes, Delete'}
              cancelText={'Cancel'}
              okButtonProps={{danger: true}}
            >
              <DeleteOutlined
                className={'cursor-pointer border p-1 font-semibold rounded-md text-red-500 hover:bg-red-50 transition-colors'}
                onClick={e => e.stopPropagation()}
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  if (!token)    {return null;}
  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-64'}>
        <Spin size={'large'} description={'Loading production data…'} />
      </div>
    );
  }
  if (error) {return <Alert message={'Failed to load production records'} type={'error'} />;}

  return (
    <div className={'min-h-screen md:p-5 p-1'}>
      {/* Header */}
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}
          >
            <ExperimentOutlined style={{fontSize: 22}} />
          </div>
          <div>
            <Title level={3} className={'header !mb-0'}>Two-Stage Manufacturing Engine</Title>
            <Text className={'text-xs text-gray-400 font-normal'}>
              Stage 1 Bulk Batches & Stage 2 Final Packaging Production
            </Text>
          </div>
        </div>

        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { refetch(); refetchBatches(); }}
            className={'btn-secondary'}
          >
            Refresh
          </Button>
          <Button
            type={'primary'}
            icon={<PlusOutlined />}
            size={'middle'}
            className={'bg-red-600 hover:!bg-red-700 text-white font-semibold rounded-lg'}
            onClick={() => setIsBatchModalVisible(true)}
          >
            + Create Bulk Batch (Stage 1)
          </Button>
          <Button
            type={'primary'}
            icon={<PlusOutlined />}
            size={'middle'}
            className={'btn-primary'}
            onClick={() => setIsRegisterModalVisible(true)}
          >
            📦 Register Packaging (Stage 2)
          </Button>
        </Space>
      </div>

      {/* Stat Cards */}
      <div className={'grid grid-cols-2 md:grid-cols-4 gap-3 mb-4'}>
        <StatCard label={'Total Packaging Invoices'} value={stats.total}     icon={<AiOutlineNodeIndex className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Active Packaging'}        value={stats.active}    icon={<FaCheck            className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Completed Packaging'}     value={stats.completed} icon={<FaBoxOpen          className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Available Batches'}       value={stats.pending}   icon={<MdOutlinePendingActions className={'bg-color'} />} bg={'#e6e9ec'} />
      </div>

      {/* Tabs: Available Bulk Batches vs Production Audit Logs */}
      <Tabs
        defaultActiveKey={'1'}
        className={'bg-white p-4 rounded-xl shadow-sm'}
        items={[
          {
            key: '1',
            label: (
              <span className={'font-bold text-sm'}>
                🧪 Stage 1: Available Bulk Batches ({availableBatches.length})
              </span>
            ),
            children: (
              <div className={'py-2'}>
                {batchesLoading ? (
                  <Spin className={'my-8 block text-center'} />
                ) : availableBatches.length === 0 ? (
                  <Alert
                    type={'info'}
                    showIcon
                    Alert={'No active bulk batches available'}
                    description={'Click "+ Create Bulk Batch (Stage 1)" above to manufacture bulk yield from raw materials.'}
                    className={'my-4'}
                  />
                ) : (
                  <div className={'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
                    {availableBatches.map(b => {
                      const percent = b.producedQuantity
                        ? Math.round((b.remainingQuantity / b.producedQuantity) * 100)
                        : 0;
                      return (
                        <Card
                          key={b.id || b.batchNumber}
                          size={'small'}
                          className={'rounded-xl border border-gray-200 shadow-sm hover:shadow transition-shadow'}
                        >
                          <div className={'flex items-center justify-between mb-2'}>
                            <Tag color={'volcano'} className={'font-mono font-bold text-xs rounded-full px-2.5 py-0.5'}>
                              {b.batchNumber}
                            </Tag>
                            <Popconfirm
                              title={'Cancel this bulk batch?'}
                              description={'Restores raw material ingredient stock.'}
                              onConfirm={() => handleCancelBatch(b.id)}
                              okText={'Yes, Cancel Batch'}
                              okButtonProps={{danger: true}}
                            >
                              <Button
                                type={'text'}
                                danger
                                icon={<CloseCircleOutlined />}
                                size={'small'}
                                className={'text-xs'}
                              >
                                Cancel Batch
                              </Button>
                            </Popconfirm>
                          </div>
                          <Text className={'font-semibold text-gray-800 text-sm block mb-1 truncate'}>
                            {b.batchName || 'Bulk Pigment Batch'}
                          </Text>
                          <div className={'my-2 bg-gray-50 p-2 rounded text-xs space-y-1'}>
                            <div className={'flex justify-between'}>
                              <span className={'text-gray-500'}>Yield Produced:</span>
                              <span className={'font-bold text-gray-700'}>{b.producedQuantity} KG</span>
                            </div>
                            <div className={'flex justify-between'}>
                              <span className={'text-gray-500'}>Remaining Available:</span>
                              <span className={'font-bold text-emerald-600'}>{b.remainingQuantity} KG</span>
                            </div>
                          </div>
                          <Progress percent={percent} size={'small'} status={percent > 20 ? 'active' : 'exception'} />
                          <div className={'mt-3 pt-2 border-t flex justify-end'}>
                            <Button
                              type={'primary'}
                              size={'small'}
                              className={'btn-primary text-xs'}
                              onClick={() => {
                                registerForm.setFieldsValue({
                                  selectedBatchIds: [b.batchNumber || b.id],
                                });
                                setIsRegisterModalVisible(true);
                              }}
                            >
                              📦 Package This Batch
                            </Button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: '2',
            label: (
              <span className={'font-bold text-sm'}>
                📋 Stage 2: Packaging Production History
              </span>
            ),
            children: (
              <div className={'py-2'}>
                <Card className={'rounded-lg border-0 shadow-sm overflow-hidden mb-3'}>
                  <div className={'flex flex-wrap gap-3 items-center justify-between'}>
                    <div className={'flex items-center gap-2'}>
                      <FilterOutlined className={'text-primary-500'} />
                      <Input.Search
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder={'Search lot number or final product…'}
                        allowClear
                        className={'w-64 rounded-xl'}
                      />
                    </div>
                    <Button
                      icon={<SyncOutlined />}
                      onClick={() => setSearchText('')}
                      className={'rounded-md text-xs'}
                    >
                      Reset Filter
                    </Button>
                  </div>
                </Card>
                <TableData
                  dataSource={filteredData}
                  tableLayout={'fixed'}
                  size={'middle'}
                  scroll={{x: 'max-content'}}
                  columns={columns}
                  rowKey={'id'}
                  onRow={record => ({onClick: () => setViewModal({visible: true, record})})}
                />
              </div>
            ),
          },
        ]}
      />

      {/* Stage 1 Bulk Batch Creation Modal */}
      <CreateBatchModal
        isModalVisible={isBatchModalVisible}
        handleCancel={() => setIsBatchModalVisible(false)}
        form={batchForm}
        token={token}
        onBatchCreated={() => {
          refetchBatches();
          toast.success('Bulk batch created successfully!');
        }}
      />

      {/* Stage 2 Final Production Packaging Registration Modal */}
      <RegisterProductionScreen
        isModalVisible={isRegisterModalVisible}
        handleCancel={() => setIsRegisterModalVisible(false)}
        form={registerForm}
        token={token}
        onProductionRegistered={() => {
          refetch();
          refetchBatches();
          toast.success('Packaging production registered!');
        }}
      />

      <ProductionDetails viewModal={viewModal} setViewModal={setViewModal} />
    </div>
  );
};

ProductionScreen.propTypes = {
  'data-row-key': PropTypes.string,
  style:          PropTypes.object,
};

export default ProductionScreen;
